<script setup lang="ts">
import { computed } from 'vue';

import type { VRouterTab } from '@components/tabs/VRouterTabs/types';
import VCard from '@components/card/VCard/VCard.vue';
import VRouterTabs from '@components/tabs/VRouterTabs/VRouterTabs.vue';
import { useI18n } from '@modules/i18n/composables';
import { useCurrentNamespace } from '@modules/permissions/composables';
import { usePermissionsStore } from '@modules/permissions/store';
import {
  PROFILE_SETTINGS_PERMISSIONS,
  TRANSLATION_GROUP_ADMIN_PROFILE_SETTINGS,
  TRANSLATION_GROUP_AFFILIATE_PROFILE_SETTINGS,
} from '@modules/profile-settings/constants';
import {
  useAdminProfileQuery,
  useAffiliateProfileQuery,
} from '@modules/profile-settings/queries';
import {
  ADMIN_PROFILE_SETTINGS_CHANGE_EMAIL_ROUTE_NAME,
  AFFILIATE_PROFILE_SETTINGS_CHANGE_EMAIL_ROUTE_NAME,
} from '@modules/profile-settings/submodules/change-email/constants';
import {
  ADMIN_PROFILE_SETTINGS_CHANGE_PASSWORD_ROUTE_NAME,
  AFFILIATE_PROFILE_SETTINGS_CHANGE_PASSWORD_ROUTE_NAME,
} from '@modules/profile-settings/submodules/change-password/constants';
import {
  ADMIN_PROFILE_SETTINGS_CONFIRMATION_EMAIL_ROUTE_NAME,
  AFFILIATE_PROFILE_SETTINGS_CONFIRMATION_EMAIL_ROUTE_NAME,
} from '@modules/profile-settings/submodules/confirmation-email/constants';
import {
  ADMIN_PROFILE_SETTINGS_GENERAL_SETTINGS_ROUTE_NAME,
  AFFILIATE_PROFILE_SETTINGS_GENERAL_SETTINGS_ROUTE_NAME,
} from '@modules/profile-settings/submodules/general-settings/constants';
import { AFFILIATE_PROFILE_SETTINGS_PAYMENT_SYSTEMS_ROUTE_NAME } from '@modules/profile-settings/submodules/payment-systems/constants';
import {
  ADMIN_PROFILE_SETTINGS_TWO_FACTOR_AUTH_ROUTE_NAME,
  AFFILIATE_PROFILE_SETTINGS_TWO_FACTOR_AUTH_ROUTE_NAME,
} from '@modules/profile-settings/submodules/two-factor-auth/constants';

const { isAdminNamespace } = useCurrentNamespace();
const { getTranslation } = useI18n();
const { hasPermission } = usePermissionsStore();

const { data: profileAdmin } = useAdminProfileQuery();
const { data: profileAffiliate } = useAffiliateProfileQuery();

const translationGroup = computed(() => (
  isAdminNamespace.value ? TRANSLATION_GROUP_ADMIN_PROFILE_SETTINGS : TRANSLATION_GROUP_AFFILIATE_PROFILE_SETTINGS
));

const tabs = computed<VRouterTab[]>(() => {
  if (isAdminNamespace.value) {
    return [{
      label: getTranslation(TRANSLATION_GROUP_ADMIN_PROFILE_SETTINGS, 'MAIN_TAB_NAME').value,
      visible: hasPermission(PROFILE_SETTINGS_PERMISSIONS.SHOW).value,
      route: {
        name: ADMIN_PROFILE_SETTINGS_GENERAL_SETTINGS_ROUTE_NAME,
      },
    }, {
      label: getTranslation(TRANSLATION_GROUP_ADMIN_PROFILE_SETTINGS, 'EMAIL_TAB_NAME').value,
      visible: hasPermission(PROFILE_SETTINGS_PERMISSIONS.SHOW).value,
      route: {
        name: ADMIN_PROFILE_SETTINGS_CHANGE_EMAIL_ROUTE_NAME,
      },
    }, {
      label: getTranslation(TRANSLATION_GROUP_ADMIN_PROFILE_SETTINGS, 'PASSWORD_TAB_NAME').value,
      visible: hasPermission(PROFILE_SETTINGS_PERMISSIONS.SHOW).value,
      route: {
        name: ADMIN_PROFILE_SETTINGS_CHANGE_PASSWORD_ROUTE_NAME,
      },
    }, {
      label: getTranslation(TRANSLATION_GROUP_ADMIN_PROFILE_SETTINGS, 'CONFIRMATION_TAB_NAME').value,
      visible: hasPermission(PROFILE_SETTINGS_PERMISSIONS.SHOW).value,
      disabled: !profileAdmin.value?.unconfirmed_email,
      route: {
        name: ADMIN_PROFILE_SETTINGS_CONFIRMATION_EMAIL_ROUTE_NAME,
      },
    }, {
      label: getTranslation(TRANSLATION_GROUP_ADMIN_PROFILE_SETTINGS, 'TFA_TAB_NAME').value,
      visible: hasPermission(PROFILE_SETTINGS_PERMISSIONS.TWO_FACTOR_AUTH).value,
      route: {
        name: ADMIN_PROFILE_SETTINGS_TWO_FACTOR_AUTH_ROUTE_NAME,
      },
    }];
  }

  return [{
    label: getTranslation(TRANSLATION_GROUP_AFFILIATE_PROFILE_SETTINGS, 'MAIN_TAB_NAME').value,
    visible: hasPermission(PROFILE_SETTINGS_PERMISSIONS.SHOW).value,
    route: {
      name: AFFILIATE_PROFILE_SETTINGS_GENERAL_SETTINGS_ROUTE_NAME,
    },
  }, {
    label: getTranslation(TRANSLATION_GROUP_AFFILIATE_PROFILE_SETTINGS, 'EMAIL_TAB_NAME').value,
    visible: hasPermission(PROFILE_SETTINGS_PERMISSIONS.SHOW).value,
    route: {
      name: AFFILIATE_PROFILE_SETTINGS_CHANGE_EMAIL_ROUTE_NAME,
    },
  }, {
    label: getTranslation(TRANSLATION_GROUP_AFFILIATE_PROFILE_SETTINGS, 'PASSWORD_TAB_NAME').value,
    visible: hasPermission(PROFILE_SETTINGS_PERMISSIONS.SHOW).value,
    route: {
      name: AFFILIATE_PROFILE_SETTINGS_CHANGE_PASSWORD_ROUTE_NAME,
    },
  }, {
    label: getTranslation(TRANSLATION_GROUP_AFFILIATE_PROFILE_SETTINGS, 'CONFIRMATION_TAB_NAME').value,
    visible: hasPermission(PROFILE_SETTINGS_PERMISSIONS.SHOW).value,
    disabled: !profileAffiliate.value?.unconfirmed_email,
    route: {
      name: AFFILIATE_PROFILE_SETTINGS_CONFIRMATION_EMAIL_ROUTE_NAME,
    },
  }, {
    label: getTranslation(TRANSLATION_GROUP_AFFILIATE_PROFILE_SETTINGS, 'PAYMENT_SYSTEMS_TAB_NAME').value,
    visible: hasPermission(PROFILE_SETTINGS_PERMISSIONS.PAYMENT_SYSTEMS).value,
    route: {
      name: AFFILIATE_PROFILE_SETTINGS_PAYMENT_SYSTEMS_ROUTE_NAME,
    },
  }, {
    label: getTranslation(TRANSLATION_GROUP_ADMIN_PROFILE_SETTINGS, 'TFA_TAB_NAME').value,
    visible: hasPermission(PROFILE_SETTINGS_PERMISSIONS.TWO_FACTOR_AUTH).value,
    route: {
      name: AFFILIATE_PROFILE_SETTINGS_TWO_FACTOR_AUTH_ROUTE_NAME,
    },
  }];
});
</script>

<template>
  <VCard v-data-qa-auto-id="'ProfileSettingsPage'"
    type="form"
    :title="$getTranslation(translationGroup, 'HEADER')"
  >
    <VRouterTabs
      :items="tabs"
      class="mb-10"
    />
    <RouterView />
  </VCard>
</template>
